'use client'

import Papa from 'papaparse'
import { useEffect, useState } from 'react'

interface SpreadsheetPreviewProps {
  url?: string
  content?: string
  maxRows?: number
}

export function SpreadsheetPreview({ url, content, maxRows = 10 }: SpreadsheetPreviewProps) {
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<string[][]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalRows, setTotalRows] = useState(0)

  useEffect(() => {
    const parseData = (csvText: string) => {
      try {
        const parsed = Papa.parse(csvText.trim(), {
          skipEmptyLines: true,
        })

        const data = parsed.data as string[][]
        if (data.length === 0) {
          setHeaders([])
          setRows([])
          setTotalRows(0)
          return
        }

        // First row as headers
        setHeaders(data[0])
        // Limit rows for preview
        const dataRows = data.slice(1, maxRows + 1)
        setRows(dataRows)
        setTotalRows(data.length - 1)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to parse CSV')
      }
    }

    if (content) {
      parseData(content)
    } else if (url) {
      setIsLoading(true)
      setError(null)
      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to fetch: ${response.statusText}`)
          }
          return response.text()
        })
        .then((text) => {
          parseData(text)
          setIsLoading(false)
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Failed to load spreadsheet')
          setIsLoading(false)
        })
    }
  }, [url, content, maxRows])

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
        <div className="p-4">
          <div className="animate-pulse space-y-2">
            <div className="h-8 rounded bg-gray-200" />
            <div className="h-6 rounded bg-gray-200" />
            <div className="h-6 rounded bg-gray-200" />
            <div className="h-6 rounded bg-gray-200" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="overflow-hidden rounded-2xl border border-red-200 bg-red-50">
        <div className="p-4 text-sm text-red-600">{error}</div>
      </div>
    )
  }

  if (headers.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
        <div className="p-4 text-sm text-gray-500">No data to display</div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="max-h-[400px] overflow-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-gray-100">
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  className="border-b border-r border-gray-200 px-3 py-2 font-semibold text-gray-700"
                >
                  {header || `Column ${index + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {headers.map((_, colIndex) => (
                  <td
                    key={colIndex}
                    className="border-b border-r border-gray-200 px-3 py-2 text-gray-900"
                  >
                    {row[colIndex] || ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalRows > maxRows && (
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-700">
          Showing {Math.min(maxRows, rows.length)} of {totalRows} rows
        </div>
      )}
    </div>
  )
}
